import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Clock, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerTimesheets() {
  const { t } = useTranslation();
  const [timesheets, setTimesheets] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    assignment_id: "",
    project_id: "",
    work_date: new Date().toISOString().split("T")[0],
    hours_worked: "",
    check_in_time: "",
    check_out_time: "",
    notes: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [timesheetsRes, assignmentsRes] = await Promise.all([
          axios.get(`${API}/timesheets`),
          axios.get(`${API}/assignments`),
        ]);
        setTimesheets(timesheetsRes.data);
        setAssignments(assignmentsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/timesheets`, {
        ...formData,
        hours_worked: parseFloat(formData.hours_worked),
      });
      toast.success(t('success.sent'));
      setDialogOpen(false);
      
      // Refresh
      const res = await axios.get(`${API}/timesheets`);
      setTimesheets(res.data);
      
      // Reset form
      setFormData({
        assignment_id: "",
        project_id: "",
        work_date: new Date().toISOString().split("T")[0],
        hours_worked: "",
        check_in_time: "",
        check_out_time: "",
        notes: "",
      });
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <DashboardLayout navItems={workerNavItems} title={t('nav.timesheets')}>
      <div className="space-y-6" data-testid="worker-timesheets">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{t('nav.timesheets')}</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                <Plus className="w-5 h-5 mr-2" />
                {t('timesheets.submitTimesheet')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-['Unbounded']">{t('timesheets.submitTimesheet')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('nav.assignments')}</Label>
                  <select
                    value={formData.assignment_id}
                    onChange={(e) => {
                      const assignment = assignments.find(a => a.assignment_id === e.target.value);
                      setFormData({
                        ...formData,
                        assignment_id: e.target.value,
                        project_id: assignment?.project_id || ""
                      });
                    }}
                    className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
                    required
                  >
                    <option value="">{t('common.select')}...</option>
                    {assignments.map((a) => (
                      <option key={a.assignment_id} value={a.assignment_id}>
                        {t('projects.title')} #{a.project_id?.slice(-8)} - {a.scheduled_date}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#94A3B8]">{t('common.date')}</Label>
                    <Input
                      type="date"
                      value={formData.work_date}
                      onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
                      className="bg-black/50 border-white/10 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#94A3B8]">{t('timesheets.hoursWorked')}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.hours_worked}
                      onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                      placeholder="8"
                      className="bg-black/50 border-white/10 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#94A3B8]">{t('timesheets.startTime')}</Label>
                    <Input
                      type="time"
                      value={formData.check_in_time}
                      onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#94A3B8]">{t('timesheets.endTime')}</Label>
                    <Input
                      type="time"
                      value={formData.check_out_time}
                      onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('common.notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('common.description') + '...'}
                    className="bg-black/50 border-white/10 text-white placeholder:text-[#475569]"
                  />
                </div>

                <Button type="submit" className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                  {t('common.submit')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Timesheets List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">{t('timesheets.myTimesheets')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : timesheets.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">{t('timesheets.noTimesheets')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {timesheets.map((timesheet) => (
                  <div
                    key={timesheet.timesheet_id}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                  >
                    <div>
                      <p className="text-white font-medium">
                        {t('projects.title')} #{timesheet.project_id?.slice(-8)}
                      </p>
                      <p className="text-sm text-[#94A3B8]">
                        {timesheet.work_date} • {timesheet.hours_worked} {t('timesheets.hoursWorked').toLowerCase()}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        statusColors[timesheet.status] || statusColors.pending
                      }`}
                    >
                      {timesheet.status === "approved" ? t('common.approved') : 
                       timesheet.status === "rejected" ? t('common.rejected') : 
                       t('common.pending')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
